import { Component, inject, Signal } from '@angular/core';
import {
  EEntityType,
  ERelationType,
  ICompanionData,
  ICorporateDesign,
  TariffProductGroup,
} from '@web/dtos';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ShoppingCartService } from '../../../../services/shopping-cart/shopping-cart.service';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { DirectTicketOwnerDataFormComponent } from '../../../../components/data-input/direct-ticket-owner-data-form/direct-ticket-owner-data-form.component';
import { ConfigurationStore } from '../../../../services/api/configuration/configuration-store.service';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import {
  EPage,
  DirectTicketConfigurationShoppingCartComponent,
} from '../../../../components/feedback/direct-ticket-configuration-shopping-cart/direct-ticket-configuration-shopping-cart.component';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { ERoute } from '../../../../constants/routes.enum';
import { CheckboxComponent } from '../../../../components/data-input/form-inputs/checkbox/checkbox.component';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { FellowTravelerStore } from '../../../../services/api/fellow-traveler/fellow-traveler-store.service';
import { AuthProvider } from '../../../../services/auth/auth.provider';
import { AuthProviderFactory } from '../../../../services/auth/auth-provider.factory';

export interface IRequiredTicketOwnerData {
  salutation: string;
  firstName: string;
  lastName: string;
  dateOfBirth: Date;
  companions?: ICompanionData[];
}

export interface ICompanionDataForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  fellowTravelerId: FormControl<string | undefined>;
}

export interface ITicketOwnerDataForm {
  isForUser: FormControl<boolean>;
  fellowTravelerId: FormControl<string | undefined>;
  salutation: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  dateOfBirth: FormControl<Date | undefined>;
  companions: FormArray<FormGroup<ICompanionDataForm>>;
}

@Component({
  selector: 'aw-dircet-ticket-owner-data-page',
  imports: [
    SectionComponent,
    ReactiveFormsModule,
    LoadingAnimationComponent,
    DirectTicketConfigurationShoppingCartComponent,
    DirectTicketOwnerDataFormComponent,
    AppendTestIdPipe,
    CheckboxComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './direct-ticket-owner-data-page.component.html',
})
export class DirectTicketOwnerDataPageComponent {
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  protected readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly router: Router = inject(Router);
  private readonly fellowTravelerStore: FellowTravelerStore =
    inject(FellowTravelerStore);

  public readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  protected readonly ticketConfiguration: Signal<
    TariffProductGroup | undefined
  > = this.shoppingCartService.directTicketConfiguration;
  public readonly isLoading: Signal<boolean> =
    this.shoppingCartService.isLoadingTicketConfiguration;

  protected readonly EPage: typeof EPage = EPage;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly RelationType: typeof ERelationType = ERelationType;
  protected readonly ticketOwnerDataForms: FormArray<
    FormArray<FormGroup<ITicketOwnerDataForm>>
  > = this.shoppingCartService.ticketOwnerDataForms;
  protected readonly isAlternativeTicketOwnerActive: Signal<boolean> =
    this.fellowTravelerStore.isActive;

  protected proceedToCheckout(): void {
    this.router.navigate([
      `/${ERoute.directTicketConfiguration}/${ERoute.checkout}/`,
    ]);
  }

  updateFormArray(): void {
    setTimeout(() => {
      this.ticketOwnerDataForms.updateValueAndValidity();
    }, 0);
  }

  protected readonly ERelationType: typeof ERelationType = ERelationType;

  protected hasAlternativeTicketOwnerOrCompanions(): boolean {
    return this.ticketOwnerDataForms.controls.some(ticketOwners =>
      ticketOwners.controls.some(
        owner =>
          !owner.controls.isForUser.value ||
          owner.controls.companions.length > 0
      )
    );
  }
}
